import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Alert, CircularProgress,
  Switch, Grid, Tabs, Tab, Divider, Stack, Avatar, alpha, IconButton,
  FormControl, Select, MenuItem,
} from '@mui/material';
import {
  Settings, Refresh, Security, Notifications, Storage, Language,
  ColorLens, Backup, Speed, CheckCircle, Email, Public, Payment,
} from '@mui/icons-material';
import { settingsAPI } from '../../api/client';
import { integerOnly, decimalOnly } from '../../utils/inputHelpers';

function AdminSystemSettingsPage() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [sys, setSys] = useState({
    site_name: 'CampusNest', site_tagline: 'Student Accommodation Made Easy',
    maintenance_mode: 'false', registration_enabled: 'true',
    email_verification: 'true', default_currency: 'GHS',
    default_language: 'en', timezone: 'Africa/Accra',
    max_upload_size_mb: '10', session_timeout_minutes: '60',
    backup_enabled: 'true', backup_frequency: 'daily',
    rate_limiting: 'true', debug_mode: 'false',
    email_notifications: 'true', push_notifications: 'true',
  });

  const [paymentSettings, setPaymentSettings] = useState({
    payment_currency: 'GHS', payment_paystack_enabled: 'true', payment_stripe_enabled: 'true',
    paystack_secret_key: '', paystack_public_key: '',
    stripe_secret_key: '', stripe_publishable_key: '',
    commission_percentage: '5',
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      settingsAPI.list({ category: 'system' }),
      settingsAPI.list({ category: 'payment' }),
    ]).then(([sysRes, payRes]) => {
      if (Array.isArray(sysRes.data)) {
        setSettings(sysRes.data);
        const m = {}; sysRes.data.forEach(s => { m[s.key] = s.value; });
        setSys(p => ({ ...p, ...m }));
      }
      if (Array.isArray(payRes.data)) {
        const pm = {}; payRes.data.forEach(s => { pm[s.key] = s.value; });
        setPaymentSettings(p => ({ ...p, ...pm }));
      }
    }).catch(() => setError('Failed to load settings'))
    .finally(() => setLoading(false));
  }, []);

  const saveSetting = async (key, value) => {
    try {
      await settingsAPI.update(key, { value: String(value) });
      setSys(p => ({ ...p, [key]: String(value) }));
      setSuccess('Setting updated');
    } catch (_) { setError('Failed to update'); }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #64748b, #94a3b8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            System Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">Core platform configuration, security, and maintenance</Typography>
        </Box>
        <IconButton onClick={() => window.location.reload()} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
      </Box>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ borderRadius: 3, mb: 3, px: 1 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} variant="scrollable" scrollButtons="auto" sx={{ '& .MuiTabs-indicator': { height: 3, borderRadius: 2 } }}>
          <Tab label="General" icon={<Settings sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Payment" icon={<Payment sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Security" icon={<Security sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Notifications" icon={<Notifications sx={{ fontSize: 18 }} />} iconPosition="start" />
          <Tab label="Maintenance" icon={<Storage sx={{ fontSize: 18 }} />} iconPosition="start" />
        </Tabs>
      </Paper>

      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Site Configuration</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Box><Typography fontWeight={600} gutterBottom>Site Name</Typography>
                  <TextField size="small" fullWidth value={sys.site_name}
                    onChange={(e)=>setSys(p=>({...p,site_name:e.target.value}))}
                    onBlur={(e)=>saveSetting('site_name', e.target.value)} />
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Tagline</Typography>
                  <TextField size="small" fullWidth value={sys.site_tagline}
                    onChange={(e)=>setSys(p=>({...p,site_tagline:e.target.value}))}
                    onBlur={(e)=>saveSetting('site_tagline', e.target.value)} />
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Default Currency</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={sys.default_currency} onChange={(e) => saveSetting('default_currency', e.target.value)}>
                      <MenuItem value="GHS">GHS (Ghana Cedi)</MenuItem>
                      <MenuItem value="USD">USD (US Dollar)</MenuItem>
                      <MenuItem value="NGN">NGN (Naira)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Default Language</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={sys.default_language} onChange={(e) => saveSetting('default_language', e.target.value)}>
                      <MenuItem value="en">English</MenuItem>
                      <MenuItem value="fr">French</MenuItem>
                      <MenuItem value="tw">Twi</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Timezone</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={sys.timezone} onChange={(e) => saveSetting('timezone', e.target.value)}>
                      <MenuItem value="Africa/Accra">Africa/Accra (GMT+0)</MenuItem>
                      <MenuItem value="Africa/Lagos">Africa/Lagos (GMT+1)</MenuItem>
                      <MenuItem value="UTC">UTC</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Registration & Access</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>User Registration</Typography><Typography variant="body2" color="text.secondary">Allow new user sign-ups</Typography></Box>
                  <Switch checked={sys.registration_enabled==='true'} onChange={(e)=>saveSetting('registration_enabled', e.target.checked?'true':'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Email Verification</Typography><Typography variant="body2" color="text.secondary">Require email verification on signup</Typography></Box>
                  <Switch checked={sys.email_verification==='true'} onChange={(e)=>saveSetting('email_verification', e.target.checked?'true':'false')} />
                </Stack>
                <Box><Typography fontWeight={600} gutterBottom>Max Upload Size (MB)</Typography>
                  <TextField size="small" type="number" value={sys.max_upload_size_mb}
                    onChange={(e)=>setSys(p=>({...p,max_upload_size_mb:e.target.value}))}
                    onKeyDown={integerOnly}
                    onBlur={(e)=>saveSetting('max_upload_size_mb', e.target.value)} sx={{ width: 120 }} />
                </Box>
              </Stack>
            </Paper>
            {settings.length > 0 && (
              <Paper sx={{ p: 3, borderRadius: 3, mt: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>Dynamic Settings</Typography><Divider sx={{ mb: 2 }} />
                <Stack spacing={2}>
                  {settings.filter(s => !['site_name','site_tagline','maintenance_mode','registration_enabled','email_verification','default_currency','default_language','timezone','max_upload_size_mb','session_timeout_minutes','backup_enabled','backup_frequency','rate_limiting','debug_mode','email_notifications','push_notifications'].includes(s.key)).map(s => (
                    <Box key={s.key}>
                      <Typography variant="subtitle2" fontWeight={600} gutterBottom>{s.description || s.key}</Typography>
                      {(s.value === 'true' || s.value === 'false') ? (
                        <Switch checked={s.value==='true'} onChange={(e)=>saveSetting(s.key, e.target.checked?'true':'false')} />
                      ) : (
                        <TextField size="small" fullWidth defaultValue={s.value}
                          onBlur={(e) => { if (e.target.value !== s.value) saveSetting(s.key, e.target.value); }} />
                      )}
                    </Box>
                  ))}
                </Stack>
              </Paper>
            )}
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Payment Gateways</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Paystack</Typography><Typography variant="body2" color="text.secondary">Enable Paystack payments (GHS, NGN)</Typography></Box>
                  <Switch checked={paymentSettings.payment_paystack_enabled==='true'} onChange={(e)=>{ const v=e.target.checked?'true':'false'; setPaymentSettings(p=>({...p,payment_paystack_enabled:v})); saveSetting('payment_paystack_enabled',v); }} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Stripe</Typography><Typography variant="body2" color="text.secondary">Enable Stripe payments (USD, international)</Typography></Box>
                  <Switch checked={paymentSettings.payment_stripe_enabled==='true'} onChange={(e)=>{ const v=e.target.checked?'true':'false'; setPaymentSettings(p=>({...p,payment_stripe_enabled:v})); saveSetting('payment_stripe_enabled',v); }} />
                </Stack>
                <Box><Typography fontWeight={600} gutterBottom>Default Currency</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={paymentSettings.payment_currency} onChange={(e)=>{ setPaymentSettings(p=>({...p,payment_currency:e.target.value})); saveSetting('payment_currency',e.target.value); }}>
                      <MenuItem value="GHS">GHS (Ghana Cedi)</MenuItem>
                      <MenuItem value="USD">USD (US Dollar)</MenuItem>
                      <MenuItem value="NGN">NGN (Naira)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Platform Commission (%)</Typography>
                  <TextField size="small" type="number" value={paymentSettings.commission_percentage}
                    onChange={(e)=>setPaymentSettings(p=>({...p,commission_percentage:e.target.value}))}
                    onKeyDown={decimalOnly}
                    onBlur={(e)=>saveSetting('commission_percentage', e.target.value)} sx={{ width: 120 }} />
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Paystack API Keys</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box><Typography fontWeight={600} gutterBottom>Live Secret Key</Typography>
                  <TextField size="small" fullWidth type="password" placeholder="sk_live_..." value={paymentSettings.paystack_secret_key}
                    onChange={(e)=>setPaymentSettings(p=>({...p,paystack_secret_key:e.target.value}))}
                    onBlur={(e)=>{ if(e.target.value) saveSetting('paystack_secret_key', e.target.value); }} />
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Live Public Key</Typography>
                  <TextField size="small" fullWidth placeholder="pk_live_..." value={paymentSettings.paystack_public_key}
                    onChange={(e)=>setPaymentSettings(p=>({...p,paystack_public_key:e.target.value}))}
                    onBlur={(e)=>{ if(e.target.value) saveSetting('paystack_public_key', e.target.value); }} />
                </Box>
              </Stack>
            </Paper>
            <Paper sx={{ p: 3, borderRadius: 3, mt: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Stripe API Keys</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={2}>
                <Box><Typography fontWeight={600} gutterBottom>Secret Key</Typography>
                  <TextField size="small" fullWidth type="password" placeholder="sk_live_..." value={paymentSettings.stripe_secret_key}
                    onChange={(e)=>setPaymentSettings(p=>({...p,stripe_secret_key:e.target.value}))}
                    onBlur={(e)=>{ if(e.target.value) saveSetting('stripe_secret_key', e.target.value); }} />
                </Box>
                <Box><Typography fontWeight={600} gutterBottom>Publishable Key</Typography>
                  <TextField size="small" fullWidth placeholder="pk_live_..." value={paymentSettings.stripe_publishable_key}
                    onChange={(e)=>setPaymentSettings(p=>({...p,stripe_publishable_key:e.target.value}))}
                    onBlur={(e)=>{ if(e.target.value) saveSetting('stripe_publishable_key', e.target.value); }} />
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Security Settings</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Rate Limiting</Typography><Typography variant="body2" color="text.secondary">Protect against API abuse</Typography></Box>
                  <Switch checked={sys.rate_limiting==='true'} onChange={(e)=>saveSetting('rate_limiting', e.target.checked?'true':'false')} />
                </Stack>
                <Box><Typography fontWeight={600} gutterBottom>Session Timeout (minutes)</Typography>
                  <TextField size="small" type="number" value={sys.session_timeout_minutes}
                    onChange={(e)=>setSys(p=>({...p,session_timeout_minutes:e.target.value}))}
                    onKeyDown={integerOnly}
                    onBlur={(e)=>saveSetting('session_timeout_minutes', e.target.value)} sx={{ width: 140 }} />
                </Box>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Security Checklist</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                {[
                  { check: 'HTTPS enforced', status: true },
                  { check: 'CORS configured', status: true },
                  { check: 'JWT token rotation', status: true },
                  { check: 'SQL injection protection', status: true },
                  { check: 'XSS prevention', status: true },
                  { check: 'Rate limiting active', status: sys.rate_limiting === 'true' },
                ].map((item, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}>
                    <CheckCircle color={item.status ? 'success' : 'disabled'} sx={{ fontSize: 18 }} />
                    <Typography variant="body2">{item.check}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Notification Channels</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6', width: 36, height: 36 }}><Email /></Avatar>
                    <Box><Typography fontWeight={600}>Email Notifications</Typography><Typography variant="body2" color="text.secondary">Send email alerts</Typography></Box>
                  </Stack>
                  <Switch checked={sys.email_notifications==='true'} onChange={(e)=>saveSetting('email_notifications', e.target.checked?'true':'false')} />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar sx={{ bgcolor: alpha('#10b981', 0.1), color: '#10b981', width: 36, height: 36 }}><Notifications /></Avatar>
                    <Box><Typography fontWeight={600}>Push Notifications</Typography><Typography variant="body2" color="text.secondary">Browser & mobile push</Typography></Box>
                  </Stack>
                  <Switch checked={sys.push_notifications==='true'} onChange={(e)=>saveSetting('push_notifications', e.target.checked?'true':'false')} />
                </Stack>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Notification Events</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={1.5}>
                {[
                  'New user registration', 'Booking confirmation', 'Payment received',
                  'KYC submission', 'Ticket created', 'Agreement signed',
                  'Property listed', 'Review posted',
                ].map((event, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="body2">{event}</Typography>
                      <Switch defaultChecked />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 4 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Maintenance</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600} color="error.main">Maintenance Mode</Typography><Typography variant="body2" color="text.secondary">Take site offline for maintenance</Typography></Box>
                  <Switch checked={sys.maintenance_mode==='true'} onChange={(e)=>saveSetting('maintenance_mode', e.target.checked?'true':'false')} color="error" />
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Debug Mode</Typography><Typography variant="body2" color="text.secondary">Enable verbose logging</Typography></Box>
                  <Switch checked={sys.debug_mode==='true'} onChange={(e)=>saveSetting('debug_mode', e.target.checked?'true':'false')} />
                </Stack>
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Backup Settings</Typography><Divider sx={{ mb: 2 }} />
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box><Typography fontWeight={600}>Automatic Backups</Typography><Typography variant="body2" color="text.secondary">Schedule database backups</Typography></Box>
                  <Switch checked={sys.backup_enabled==='true'} onChange={(e)=>saveSetting('backup_enabled', e.target.checked?'true':'false')} />
                </Stack>
                <Box><Typography fontWeight={600} gutterBottom>Backup Frequency</Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={sys.backup_frequency} onChange={(e) => saveSetting('backup_frequency', e.target.value)}>
                      <MenuItem value="hourly">Hourly</MenuItem>
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography fontWeight={600} gutterBottom>System Health</Typography>
                  <Stack spacing={1}>
                    {[
                      { item: 'Database', status: 'Healthy' },
                      { item: 'Storage', status: '45% used' },
                      { item: 'API Server', status: 'Running' },
                      { item: 'Cache', status: 'Active' },
                    ].map((h, i) => (
                      <Stack key={i} direction="row" justifyContent="space-between">
                        <Typography variant="body2">{h.item}</Typography>
                        <Typography variant="body2" fontWeight={600} color="success.main">{h.status}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

export default AdminSystemSettingsPage;
